import { useQuery, useMutation } from '@tanstack/react-query';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

enum QueryKeys {
    FileUploadSettings = 'file-upload-settings',
}

export interface FileUploadSettings {
    maxFileSizeMB: number;
    allowedFileTypes: string;
    maxFilesPerBatch: number;
}

export interface UserInfo {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    companyName: string;
}

interface ApiResponse<T> {
    result: T;
    statusCode: number;
}

export const useFileUploadSettingsQuery = () =>
    useQuery({
        queryKey: [QueryKeys.FileUploadSettings],
        queryFn: async (): Promise<FileUploadSettings> => {
            const response = await fetch(`${API_BASE_URL}/fileupload/settings`);

            if (!response.ok) {
                throw new Error('Failed to fetch file upload settings');
            }

            const data: ApiResponse<FileUploadSettings> = await response.json();
            return data.result;
        },
        staleTime: 1000 * 60 * 30, // Cache for 30 minutes
        retry: 2,
    });

export const useUpdateBatchUserInfoMutation = () =>
    useMutation({
        mutationFn: async ({ batchId, userInfo }: { batchId: string; userInfo: UserInfo }) => {
            const response = await fetch(`${API_BASE_URL}/fileupload/batch/${batchId}/userinfo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userInfo),
            });

            if (!response.ok) {
                let errorMessage = 'Failed to save user information';
                try {
                    const responseText = await response.text();
                    const errorData = JSON.parse(responseText);
                    if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                        errorMessage = errorData.errors.join(', ');
                    }
                } catch {
                    errorMessage = 'Failed to save user information';
                }
                throw new Error(errorMessage);
            }

            return response.json();
        },
    });
